'use strict'

// A calendar view places each record on the day of its date column and
// shows the chosen title and description. The live app and tests share this.

const { formattingToStyle } = require('./display-rules')
const { readMultiselectItems } = require('./multiselect-display')

const CALENDAR_ROW_LIMIT = 1000

function pad (number) {

  return String(number).padStart(2, '0')
}

function formatDay (year, month, day) {

  return `${year}-${pad(month)}-${pad(day)}`
}

function calendarDayKey (value) {

  if (value == null || value === '') return null

  if (value instanceof Date) {

    if (Number.isNaN(value.getTime())) return null

    const utcMidnight = value.getUTCHours() === 0
      && value.getUTCMinutes() === 0
      && value.getUTCSeconds() === 0
      && value.getUTCMilliseconds() === 0

    if (utcMidnight) {

      return formatDay(value.getUTCFullYear(), value.getUTCMonth() + 1, value.getUTCDate())
    }

    return formatDay(value.getFullYear(), value.getMonth() + 1, value.getDate())
  }

  const text = String(value).trim()

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text

  if (/^\d{4}-\d{2}-\d{2}[ T]00:00:00(\.0+)?$/.test(text)) return text.slice(0, 10)

  // Date columns are stored as UTC midnight. That instant is the previous
  // evening in time zones behind UTC, so the calendar day is the date prefix.
  if (/^\d{4}-\d{2}-\d{2}T00:00:00(?:\.0+)?Z$/.test(text)) return text.slice(0, 10)

  const parsed = new Date(text)

  if (Number.isNaN(parsed.getTime())) return null

  return formatDay(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate())
}

function eventText (value) {

  if (value == null) return ''

  if (typeof value === 'string' && readMultiselectItems(value)) return eventText(readMultiselectItems(value))

  if (value instanceof Date) return calendarDayKey(value) || ''

  if (Array.isArray(value)) {

    return value.map(eventText).filter(Boolean).join(', ')
  }

  if (typeof value === 'object') return ''

  return String(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

function calendarColumnNames (view) {

  if (!view || view.type !== 'calendar') return []

  const names = []

  for (const name of [view.dateColumn, view.titleColumn, view.descriptionColumn, view.recurrenceColumn]) {

    if (name && names.indexOf(name) === -1) names.push(name)
  }

  return names
}

function stampCalendarEvent (displayRow, sourceRow, view) {

  if (!displayRow || !sourceRow || !view || view.type !== 'calendar') return displayRow

  displayRow._calendarInstant = view.dateColumn ? sourceRow[view.dateColumn] : null
  displayRow._calendarDate = view.dateColumn ? calendarDayKey(sourceRow[view.dateColumn]) : null
  displayRow._calendarTitle = view.titleColumn ? eventText(sourceRow[view.titleColumn]) : ''
  displayRow._calendarDescription = view.descriptionColumn ? eventText(sourceRow[view.descriptionColumn]) : ''
  displayRow._calendarRule = view.recurrenceColumn ? sourceRow[view.recurrenceColumn] : null

  if (sourceRow._formatting) displayRow._formatting = sourceRow._formatting

  return displayRow
}

function calendarFieldStyle (formatting, column) {

  const fmt = formatting && column ? formatting[column] : null

  if (!fmt) return { hidden: false, style: {} }

  return {
    hidden: !!fmt.hidden,
    style: formattingToStyle(fmt)
  }
}

module.exports = {
  CALENDAR_ROW_LIMIT,
  calendarDayKey,
  calendarColumnNames,
  stampCalendarEvent,
  calendarFieldStyle,
  eventText
}
